import { FormEvent } from 'react';
import { FormControl } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import MultiSelectField from 'components/forms/multiSelectField/MultiSelectField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import { ALL_WEEK_DAYS } from 'models/DayOfWeek';
import {
  ConditionType,
  TimeOperator,
  TimeType,
  TriggerEventType,
  TriggerTimeCondition,
} from 'models/automation/Triggers';
import { TriggerConditionsContainer } from '../TriggersTab.styles';
import { TriggerAddEditModalFormValues } from './TriggerAddEditModal.schema';

const TriggerAddEditModalTimeConditionExtraFields = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TriggerAddEditModalFormValues>();
  const timeConditionType = useWatch({ control, name: 'timeCondition.type' });

  const conditionType = useWatch({
    control,
    name: 'timeCondition.condition.conditionType',
  });

  const operator = useWatch({
    control,
    name: 'timeCondition.condition.operator',
  });

  const timeTypeOptions = Object.values(TimeType).map((type) => ({
    value: type,
    label: t('pageAutomations.triggers.timeType', { context: type }),
  }));

  const checkInOptions = [
    ConditionType.BEFORE_STAY_STARTS,
    ConditionType.AFTER_STAY_STARTS,
  ].map((type) => ({
    value: type,
    label: t('pageAutomations.triggers.conditionType', { context: type }),
  }));

  const checkoutOptions = [
    ConditionType.BEFORE_STAY_ENDS,
    ConditionType.AFTER_STAY_ENDS,
  ].map((type) => ({
    value: type,
    label: t('pageAutomations.triggers.conditionType', { context: type }),
  }));

  const operatorOptions = (
    conditionType === ConditionType.AFTER_STAY_ENDS ||
    conditionType === ConditionType.AFTER_STAY_STARTS
      ? [TimeOperator.EQUAL]
      : [
          TimeOperator.LESS_THAN_OR_EQUAL,
          TimeOperator.EQUAL,
          TimeOperator.GREATER_THAN_OR_EQUAL,
        ]
  ).map((value) => ({
    value,
    label: t('pageAutomations.triggers.timeOperator', { context: value }),
  }));

  const dayOfWeekOptions = ALL_WEEK_DAYS.map((weekDay) => ({
    label: t(`common.dayOfWeek.${weekDay}`),
    value: weekDay,
  }));

  const handleConditionChange = (event: FormEvent<FormControl>) => {
    const { value } = event.target as HTMLSelectElement;

    switch (value) {
      case ConditionType.AFTER_STAY_ENDS:
        setValue('eventType', TriggerEventType.AFTER_STAY_ENDS);
        break;
      case ConditionType.AFTER_STAY_STARTS:
        setValue('eventType', TriggerEventType.AFTER_STAY_STARTS);
        break;
      case ConditionType.BEFORE_STAY_ENDS:
        setValue('eventType', TriggerEventType.BEFORE_STAY_ENDS);
        break;
      case ConditionType.BEFORE_STAY_STARTS:
        setValue('eventType', TriggerEventType.BEFORE_STAY_STARTS);
        break;
      default:
        break;
    }
  };

  if (timeConditionType === TriggerTimeCondition.CHECK_IN) {
    return (
      <TriggerConditionsContainer>
        <div
          className="condition-row condition-row__fields"
          data-testid="condition-row"
        >
          <TextField
            aria-label={t('common.time')}
            colSmInput={12}
            defaultValue={1}
            min={0}
            name="timeCondition.condition.timeInMilliSeconds"
            step={1}
            type="number"
          />
          <SelectField
            aria-label={t('pageAutomations.triggers.timeType')}
            colSmInput={12}
            name="timeCondition.condition.timeType"
            options={timeTypeOptions}
          />
          <SelectField
            aria-label={t('pageAutomations.triggers.timeOperator')}
            colSmInput={12}
            disabled={operatorOptions.length <= 1}
            name="timeCondition.condition.operator"
            options={operatorOptions}
          />
          <SelectField
            aria-label={t('pageAutomations.triggers.conditionType')}
            colSmInput={12}
            defaultValue={ConditionType.BEFORE_STAY_STARTS}
            name="timeCondition.condition.conditionType"
            onChangeHandler={handleConditionChange}
            options={checkInOptions}
          />

          <p className="condition-help-text">
            {t('pageAutomations.triggers.modal.checkIn')}
          </p>
        </div>

        {conditionType === ConditionType.BEFORE_STAY_STARTS &&
          operator === TimeOperator.LESS_THAN_OR_EQUAL && (
            <div
              className="condition-row condition-row__fields"
              data-testid="condition-row"
            >
              <p className="condition-help-text">
                {t('pageAutomations.triggers.modal.backupTime')}
              </p>

              <TextField
                aria-label={t('pageAutomations.triggers.modal.backupTime')}
                colSmInput={12}
                defaultValue={1}
                min={0}
                name="timeCondition.condition.backupTimeInMilliSeconds"
                step={1}
                type="number"
              />

              <p className="condition-help-text">
                {t('pageAutomations.triggers.modal.hoursPastCheckIn')}
              </p>
            </div>
          )}
      </TriggerConditionsContainer>
    );
  }

  if (timeConditionType === TriggerTimeCondition.CHECK_OUT) {
    return (
      <TriggerConditionsContainer>
        <div
          className="condition-row condition-row__fields"
          data-testid="condition-row"
        >
          <TextField
            aria-label={t('common.time')}
            colSmInput={12}
            defaultValue={1}
            min={0}
            name="timeCondition.condition.timeInMilliSeconds"
            step={1}
            type="number"
          />
          <SelectField
            aria-label={t('pageAutomations.triggers.timeType')}
            colSmInput={12}
            name="timeCondition.condition.timeType"
            options={timeTypeOptions}
          />
          <SelectField
            aria-label={t('pageAutomations.triggers.timeOperator')}
            colSmInput={12}
            disabled={operatorOptions.length <= 1}
            name="timeCondition.condition.operator"
            options={operatorOptions}
          />
          <SelectField
            aria-label={t('pageAutomations.triggers.conditionType')}
            colSmInput={12}
            defaultValue={ConditionType.BEFORE_STAY_ENDS}
            name="timeCondition.condition.conditionType"
            onChangeHandler={handleConditionChange}
            options={checkoutOptions}
          />

          <p className="condition-help-text">
            {t('pageAutomations.triggers.modal.checkout')}
          </p>
        </div>
      </TriggerConditionsContainer>
    );
  }

  if (timeConditionType === TriggerTimeCondition.DAY_OF_WEEK) {
    return (
      <TriggerConditionsContainer>
        <div
          className="condition-row condition-row__fields condition-row__fields--space-between"
          data-testid="condition-row"
        >
          <SelectField
            aria-label={t('pageAutomations.triggers.conditionType')}
            colSmInput={12}
            defaultValue={ConditionType.DAY_OF_WEEK}
            disabled
            name="timeCondition.condition.conditionType"
            options={[
              {
                value: ConditionType.DAY_OF_WEEK,
                label: t('pageAutomations.triggers.conditionType', {
                  context: ConditionType.DAY_OF_WEEK,
                }),
              },
            ]}
          />
          <p className="condition-help-text">
            {t('pageAutomations.triggers.conditionType', {
              context: ConditionType.AND,
            })}
          </p>
        </div>

        <div
          className="condition-row condition-row__fields"
          data-testid="condition-row"
        >
          <p className="condition-help-text">
            {t('pageAutomations.triggers.modal.dayOfTheWeekIs')}
          </p>

          <MultiSelectField
            aria-label={t('pageAutomations.triggers.modal.dayOfTheWeekIs')}
            colSmInput={12}
            isSelectAllEnabled
            name="timeCondition.condition.daysOfWeek"
            options={dayOfWeekOptions}
          />
        </div>
      </TriggerConditionsContainer>
    );
  }

  return null;
};

export default TriggerAddEditModalTimeConditionExtraFields;
